import { useState } from 'react'
import { formatCompactCount } from '@bloo-team/api-client'
import { Button, Icon } from '@bloo-team/ui'
import { X } from '@bloo-team/ui/icons'
import { SlideOver } from '@bloo-team/ui'
import { AiTriageCard } from './AiTriageCard'
import { SignalTagChips } from './SignalTagChips'
import { SignalPane } from './SignalPane'
import { EntityList, NotesAudit, PriorHandling, Section, StorylineMembers, useSignalDerived } from './SignalSections'
import { absTime } from '../../lib/local-time'
import { DISPOSITION_LABEL, type SignalTriage } from '../../lib/signal-model'
import { agoLabel } from './panel-utils'
import type { SignalPanelProps } from './types'

export { agoLabel, copyText, entitySearchQuery, signalLink } from './panel-utils'

/**
 * The signal panel (docs/signals.md §The signal pane and panel). Two
 * placements: `docked` — the pane beside the queue, a header and tabs
 * (SignalPane); `sheet` — the full panel in a SlideOver, one long scroll:
 * header · reason · AI card · entities · storyline · raw events (+ the deep
 * panel the page hands in) · pinned rows · prior
 * handling · notes/audit · raw json; the footer carries the exit actions
 * with their key hints.
 *
 * Props-in/callbacks-out: every write (note, unpin, close, escalate)
 * is a callback to the host page — the panel derives, it never stores.
 */

export const STATUS_LABEL: Record<SignalTriage['status'], string> = {
  open: 'open',
  in_progress: 'in progress',
  closed: 'closed',
}

export function SignalPanel(props: SignalPanelProps) {
  if (props.variant === 'docked') return <SignalPane {...props} />
  return <SignalSheet {...props} />
}

function SignalSheet(props: SignalPanelProps) {
  const {
    signal,
    triage,
    storyline,
    outsideTab,
    tags,
    deepOpen,
    deep,
    suppressions,
    baseline,
    entityContext,
    onClose,
    onOpenEntity,
    onToggleDeep,
    onOpenAsHunt,
    onEscalate,
    onCloseSignal,
    onSuppress,
    onReopen,
    onAddNote,
    onUnpin,
    onOpenSearch,
    onOpenDetection,
    onSelectSignal,
    suggestion,
    onAcceptSuggestion,
    onSuggestionNotHelpful,
    onOpenWorkbook,
    workbookBusy = false,
    workbookRefusal = null,
  } = props
  const [showJson, setShowJson] = useState(false)
  const derived = useSignalDerived(props)

  const status = triage?.status ?? 'open'
  const closed = status === 'closed'

  const chips = [signal.tactic, signal.technique, signal.subtechnique].filter(Boolean)

  const actions = (
    <>
      {closed ? (
        <Button variant="outline" size="sm" tone="neutral" onPress={onReopen}>
          Reopen
        </Button>
      ) : (
        <Button variant="solid" size="sm" onPress={onCloseSignal}>
          Close… <kbd className="sp-key">x</kbd>
        </Button>
      )}
      <Button variant="outline" size="sm" tone="neutral" onPress={onSuppress}>
        Suppress… <kbd className="sp-key">s</kbd>
      </Button>
      <Button variant="outline" size="sm" tone="neutral" onPress={onEscalate}>
        Add to case
      </Button>
      <Button variant="ghost" size="sm" tone="neutral" onPress={onOpenAsHunt}>
        Open as hunt <kbd className="sp-key">h</kbd>
      </Button>
      {onOpenWorkbook ? (
        <Button variant="ghost" size="sm" tone="neutral" onPress={onOpenWorkbook} isDisabled={workbookBusy || !signal.detectionId}>
          {workbookBusy ? 'Checking lock…' : 'Open workbook'}
        </Button>
      ) : null}
      <Button variant="ghost" size="sm" tone="neutral" onPress={onOpenDetection}>
        Open detection
      </Button>
    </>
  )

  return (
    <SlideOver open onClose={onClose} eyebrow={`signal · ${signal.uuid}`} title={signal.detectionName || 'Detection'} actions={actions} wide>
      {workbookRefusal ? (
        <div className="bui-alert bui-alert-error sg-lock-msg" role="alert">
          <span className="bui-alert-mark">✕</span>
          <span>
            <span className="bui-alert-title">Workbook not opened</span>
            {workbookRefusal}
          </span>
        </div>
      ) : null}
      {/* ---------------------------------------------------------- header */}
      <div className="sp-head" data-severity={signal.severity}>
        <div className="sp-head-row">
          <span className="sp-sev" data-severity={signal.severity} aria-hidden="true" />
          <span className="sp-sev-label">{signal.severity}</span>
          {signal.score !== null ? <span className="sp-score">{signal.score}/10</span> : null}
          {signal.confidence ? <span className="hud-unit">confidence {signal.confidence}</span> : null}
          <span className="sp-status" data-status={status}>
            {STATUS_LABEL[status]}
            {triage?.disposition ? ` · ${DISPOSITION_LABEL[triage.disposition]}` : ''}
          </span>
        </div>
        {outsideTab ? <p className="hud-unit muted" role="note">not in this queue tab</p> : null}
        {signal.stage || tags?.length ? (
          <div className="hud-row sg-head-tags">
            {tags?.length ? <SignalTagChips tags={tags} /> : null}
            {signal.stage ? (
              <span className="sg-stage" data-stage={signal.stage}>
                {signal.stage}
              </span>
            ) : null}
          </div>
        ) : null}
        {chips.length ? (
          <div className="sp-chips" aria-label="ATT&CK">
            {chips.map((c, i) => (
              <span key={c} className="sp-chip">
                {i > 0 ? <span className="sp-chip-sep">›</span> : null}
                {c}
              </span>
            ))}
          </div>
        ) : null}
        <dl className="kv sp-kv">
          <dt>time</dt>
          <dd className="mono">
            {absTime(signal.at)} <span className="hud-unit">· {agoLabel(signal.at)}</span>
          </dd>
          <dt>occurrences</dt>
          {/* Live counters run to six figures (signals-capture.md) — compact, exact on hover. */}
          <dd className="mono" title={String(signal.occurrences)}>
            {formatCompactCount(signal.occurrences)}
          </dd>
          <dt>streams</dt>
          <dd className="mono">{signal.streams.length ? signal.streams.join(', ') : '—'}</dd>
          {signal.remark ? (
            <>
              <dt>remark</dt>
              <dd>{signal.remark}</dd>
            </>
          ) : null}
          {triage?.huntId ? (
            <>
              <dt>hunt</dt>
              <dd className="mono">hunt opened · {triage.huntId}</dd>
            </>
          ) : null}
        </dl>
      </div>

      <Section eyebrow="reason">
        <p className="sp-reason">{derived.reason}</p>
      </Section>

      {suggestion ? (
        <Section eyebrow="AI triage suggestion">
          <AiTriageCard suggestion={suggestion} onAccept={onAcceptSuggestion} onNotHelpful={onSuggestionNotHelpful} />
        </Section>
      ) : null}

      <Section eyebrow={`entities · ${signal.entities.length}`}>
        <EntityList signal={signal} derived={derived} entityContext={entityContext} onOpenEntity={onOpenEntity} onOpenSearch={onOpenSearch} />
      </Section>

      <Section
        eyebrow={
          <>
            storyline · {derived.memberCount}
            {storyline ? <b>{storyline.title}</b> : null}
          </>
        }
      >
        <StorylineMembers signal={signal} derived={derived} onSelectSignal={onSelectSignal} />
      </Section>

      <Section
        eyebrow="raw events"
        aside={
          <span className="hud-row sp-aside">
            <Button variant={deepOpen ? 'outline' : 'solid'} size="sm" onPress={onToggleDeep}>
              {deepOpen ? 'Hide investigation' : 'Investigate'}
            </Button>
            <Button variant="ghost" size="sm" tone="neutral" onPress={() => onOpenSearch(signal.rawEventQuery)}>
              Open in search
            </Button>
          </span>
        }
      >
        <code className="sp-query">{signal.rawEventQuery || '— no raw_event_query on this signal —'}</code>
        {deepOpen && deep ? <div className="sp-deep">{deep}</div> : null}
      </Section>

      {triage && triage.pins.length > 0 ? (
        <Section eyebrow={`pinned rows · ${triage.pins.length}`}>
          <div className="hud-stack">
            {triage.pins.map((p) => (
              <div key={p.id} className="hud-panel sp-pin">
                <div className="hud-row hud-row-between">
                  <span className="hud-unit" title={p.sourceQuery}>
                    {agoLabel(p.at)}
                  </span>
                  <button type="button" className="qbc-x" aria-label="Unpin row" onClick={() => onUnpin(p.id)}>
                    <Icon icon={X} size={10} />
                  </button>
                </div>
                <dl className="kv">
                  {Object.entries(p.excerpt)
                    .slice(0, 6)
                    .map(([k, v]) => (
                      <div key={k} style={{ display: 'contents' }}>
                        <dt>{k}</dt>
                        <dd className="mono">{v}</dd>
                      </div>
                    ))}
                </dl>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      <Section eyebrow={`prior handling · this detection · ${derived.poolLabel}`}>
        <PriorHandling derived={derived} suppressions={suppressions} baseline={baseline} />
      </Section>

      <Section eyebrow={`notes · audit · ${derived.audit.length}`}>
        <NotesAudit key={signal.uuid} derived={derived} onAddNote={onAddNote} />
      </Section>

      <Section
        eyebrow="raw json"
        aside={
          <button type="button" className="list-chip" aria-expanded={showJson} onClick={() => setShowJson((v) => !v)}>
            {showJson ? 'hide raw json' : 'show raw json'}
          </button>
        }
      >
        {showJson ? <pre className="sp-json">{JSON.stringify(signal.raw, null, 2)}</pre> : null}
      </Section>
    </SlideOver>
  )
}
